import { createFileRoute } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Upload, Download, Trash2, History, Search } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  listMyFiles,
  recordMyFile,
  getMyFileUrl,
  trashMyFile,
  listFileVersions,
} from "@/lib/api/student-portal.functions";
import { WORKSPACE_FOLDERS, folderLabel, WORKSPACE_BUCKET } from "@/lib/student-portal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/portal/student/workspace")({
  component: WorkspacePage,
});

const MAX_BYTES = 25 * 1024 * 1024;

function WorkspacePage() {
  const list = useServerFn(listMyFiles);
  const record = useServerFn(recordMyFile);
  const signUrl = useServerFn(getMyFileUrl);
  const trash = useServerFn(trashMyFile);
  const versions = useServerFn(listFileVersions);
  const qc = useQueryClient();

  const [folder, setFolder] = useState<string>("assignments");
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const [openVersions, setOpenVersions] = useState<{ id: string; rows: any[] } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const q = useQuery({
    queryKey: ["student-files", folder, search],
    queryFn: () => list({ data: { folder, search: search || undefined } }),
  });

  async function onUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error("File too large", { description: "Maximum size is 25 MB." });
      return;
    }
    setUploading(true);
    try {
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth.user?.id;
      if (!uid) throw new Error("Not signed in");
      const safe = file.name.replace(/[^\w.\-]+/g, "_");
      const path = `${uid}/${folder}/${Date.now()}-${safe}`;
      const { error } = await supabase.storage.from(WORKSPACE_BUCKET).upload(path, file, {
        upsert: false,
        contentType: file.type || "application/octet-stream",
      });
      if (error) throw error;
      await record({
        data: {
          folder,
          title: file.name.replace(/\.[^.]+$/, ""),
          storage_path: path,
          original_filename: file.name,
          mime_type: file.type || "",
          size_bytes: file.size,
        },
      });
      toast.success("Uploaded to your workspace");
      qc.invalidateQueries({ queryKey: ["student-files"] });
      qc.invalidateQueries({ queryKey: ["student-dashboard"] });
    } catch (err: any) {
      toast.error("Upload failed", { description: err?.message });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function download(id: string) {
    try {
      const { url } = await signUrl({ data: { id } });
      if (url) window.open(url, "_blank", "noopener");
    } catch (err: any) {
      toast.error("Could not open file", { description: err?.message });
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label="Workspace folder"
          value={folder}
          onChange={(e) => setFolder(e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
        >
          {WORKSPACE_FOLDERS.map((f) => (
            <option key={f} value={f}>
              {folderLabel(f)}
            </option>
          ))}
        </select>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            aria-label="Search documents"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search documents"
            className="pl-8"
          />
        </div>
        <Button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="bg-navy hover:bg-navy/90"
        >
          {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
          Upload
        </Button>
        <input ref={fileRef} type="file" className="hidden" onChange={onUpload} />
      </div>

      <p className="text-xs text-muted-foreground">
        Files are private to you, versioned on replacement, and never permanently deleted — removing a
        file only moves it out of view.
      </p>

      {q.isLoading ? (
        <Loader2 className="h-5 w-5 animate-spin text-navy" />
      ) : (q.data?.files.length ?? 0) === 0 ? (
        <p className="text-sm text-muted-foreground">No documents in {folderLabel(folder)} yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Title</th>
                <th className="px-3 py-2">Version</th>
                <th className="px-3 py-2">Uploaded</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {q.data!.files.map((f: any) => (
                <tr key={f.id} className="border-t border-border">
                  <td className="px-3 py-2">
                    <span className="font-medium text-navy">{f.title}</span>
                    <span className="block text-xs text-muted-foreground">{f.original_filename}</span>
                  </td>
                  <td className="px-3 py-2 text-xs">v{f.version ?? 1}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {new Date(f.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        aria-label="Download"
                        onClick={() => download(f.id)}
                        className="rounded-md border border-input p-1.5 hover:border-navy"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        aria-label="Version history"
                        onClick={async () => {
                          const res = await versions({ data: { id: f.id } });
                          setOpenVersions({ id: f.id, rows: res.versions });
                        }}
                        className="rounded-md border border-input p-1.5 hover:border-navy"
                      >
                        <History className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        aria-label="Remove from view"
                        onClick={async () => {
                          await trash({ data: { id: f.id } });
                          toast.success("Moved out of view (kept on record)");
                          qc.invalidateQueries({ queryKey: ["student-files"] });
                        }}
                        className="rounded-md border border-input p-1.5 text-destructive hover:border-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openVersions && (
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg text-navy">Version history</h3>
            <button className="text-sm text-muted-foreground" onClick={() => setOpenVersions(null)}>
              Close
            </button>
          </div>
          <ul className="mt-2 space-y-1 text-sm">
            {openVersions.rows.map((v: any) => (
              <li key={v.id} className="flex items-center justify-between gap-3">
                <span>
                  v{v.version} — {v.original_filename}
                </span>
                <button className="text-navy hover:underline" onClick={() => download(v.id)}>
                  Open
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
